import { useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';
import { PALETTE, PALETTE_SIZE } from '../../lib/colorPalette';

/**
 * Compact 5×N palette grid anchored under a swatch button. Shared by
 * the WBS catalog editor (領域 / ベンダー) and the Settings assignee
 * list so all three surfaces use the same picker — same hit-target
 * size, same outside-click handling, same selected-state ring.
 *
 * Position is `absolute` from the parent. The parent is responsible
 * for `position: relative` and any z-index it wants. Outside-click is
 * handled internally; the parent only has to swap `open` state when
 * `onPick` fires.
 */
export function ColorPickerPopover({
  selected,
  onPick,
  onClose,
  className,
}: {
  selected: number;
  onPick: (colorIndex: number) => void;
  onClose: () => void;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onClose();
    };
    // Defer one tick so the click that opened us doesn't close us.
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onClick);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onClick);
    };
  }, [onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className={cn(
        'absolute z-50 grid grid-cols-5 gap-1 rounded-md border border-slate-200 bg-white p-2 shadow-lg',
        className,
      )}
      role="dialog"
      aria-label="色を選択"
      data-testid="color-picker-popover"
      onClick={(e) => e.stopPropagation()}
    >
      {PALETTE.map((cls, i) => {
        const isSelected = i === selected;
        return (
          <button
            key={i}
            type="button"
            aria-label={`色 ${i + 1}`}
            aria-pressed={isSelected}
            data-color-index={i}
            onClick={() => onPick(i)}
            className={cn(
              'h-6 w-6 rounded ring-1 ring-inset ring-slate-200 hover:ring-slate-400',
              cls,
              isSelected ? 'ring-2 ring-slate-700' : '',
            )}
          />
        );
      })}
      <span className="sr-only">合計 {PALETTE_SIZE} 色</span>
    </div>
  );
}
